// src/features/developer/utils/timeUtils.ts

import {
    format as formatDateFns,
    fromUnixTime,
    getTime,
    getUnixTime,
    isValid,
    startOfDay,
    endOfDay,
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear,
} from 'date-fns';
import { formatInTimeZone, toDate } from 'date-fns-tz';

// --- Constants ---
export const DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss.SSS';
export const UTC_TIMEZONE = 'UTC';

// --- Types ---
export interface BoundaryResult {
    startOfDayTs: number;
    endOfDayTs: number;
    startOfMonthTs: number;
    endOfMonthTs: number;
    startOfYearTs: number;
    endOfYearTs: number;
}

// --- Core Conversion Functions ---

/**
 * Converts a Unix timestamp (seconds or ms) to a Date object.
 * Auto-detects if the timestamp is in seconds (10 digits) or milliseconds (13 digits).
 * @param {number | string} ts - The timestamp.
 * @returns {Date | null} A Date object or null if invalid.
 */
export const timestampToDate = (ts: number | string): Date | null => {
    const numTs = Number(ts);
    if (isNaN(numTs) || numTs < 0) return null;

    // A simple heuristic to detect seconds vs. milliseconds.
    // If the number has 10 or fewer digits, assume seconds.
    const date = String(Math.abs(numTs)).length <= 10 ? fromUnixTime(numTs) : new Date(numTs);

    return isValid(date) ? date : null;
};

/**
 * Formats a Date object into a string for a given timezone.
 * @param {Date} date - The date to format.
 * @param {string} timeZone - The IANA timezone string.
 * @returns {string} The formatted date string.
 */
export const getFormattedDateInTz = (date: Date, timeZone: string): string => {
    try {
        return formatInTimeZone(date, timeZone, DATE_FORMAT);
    } catch (error) {
        console.error(`Error formatting date in timezone ${timeZone}:`, error);
        return 'Invalid Date';
    }
};

/**
 * Converts seconds into a duration object.
 * @param {number} totalSeconds - The total number of seconds.
 * @returns An object with days, hours, minutes, and seconds, or null if invalid.
 */
export const secondsToDuration = (
    totalSeconds: number
): { days: number; hours: number; minutes: number; seconds: number } | null => {
    if (isNaN(totalSeconds) || totalSeconds < 0) {
        return null;
    }

    const days = Math.floor(totalSeconds / (3600 * 24));
    totalSeconds %= 3600 * 24;
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return { days, hours, minutes, seconds };
};

/**
 * Calculates the start and end timestamps for the day, month, and year of a given date in a specific timezone.
 * @param {Date} date - The reference date.
 * @param {string} timeZone - The IANA timezone string.
 * @returns {BoundaryResult} An object containing all boundary timestamps in seconds.
 */
export const calculateDateBoundaries = (date: Date, timeZone: string): BoundaryResult => {
    // Convert the local date to the target timezone to get the correct "day"
    const zonedDate = toDate(date, { timeZone });

    // Calculate boundaries based on the zoned date
    const startOfDayDate = startOfDay(zonedDate);
    const endOfDayDate = endOfDay(zonedDate);
    const startOfMonthDate = startOfMonth(zonedDate);
    const endOfMonthDate = endOfMonth(zonedDate);
    const startOfYearDate = startOfYear(zonedDate);
    const endOfYearDate = endOfYear(zonedDate);

    // Convert boundaries back to Unix timestamps (seconds)
    return {
        startOfDayTs: getUnixTime(startOfDayDate),
        endOfDayTs: getUnixTime(endOfDayDate),
        startOfMonthTs: getUnixTime(startOfMonthDate),
        endOfMonthTs: getUnixTime(endOfMonthDate),
        startOfYearTs: getUnixTime(startOfYearDate),
        endOfYearTs: getUnixTime(endOfYearDate),
    };
};